import type {
  FullProfile,
  ProfileSummary,
  SwipeRequest,
  SwipeResponse,
  Match,
  Message,
  BootstrapResponse,
  ApiResponse,
  PaginatedResponse,
  OnboardingData,
  VerificationStatusResponse,
  StartTestResponse,
  TestDataResponse,
  SubmitAnswersRequest,
  SubmitAnswersResponse,
  TestResultResponse,
  CardResponse,
  SwipeCreateRequest,
  SwipeResponseData,
  LikesResponse,
  MyLikesResponse,
  RemoveLikeRequest,
  RemoveLikeResponse,
  MatchResponse,
  MessageResponse,
  NotificationCounters,
  SubscriptionStatusResponse,
  BasicFilters,
  BasicFiltersResponse,
  MessageCreate,
  MessageUpdate,
  MessageReport,
} from '@/types';

/**
 * ITinder API client interface
 */
export interface ITinderApiClient {
  // Token management
  setToken(token: string): void;
  
  // Authentication & Bootstrap
  bootstrap(initData?: string): Promise<BootstrapResponse>;
  
  // Profile management
  createProfile(data: Partial<OnboardingData>): Promise<FullProfile>;
  updateProfile(data: Partial<FullProfile>): Promise<FullProfile>;
  updateGender(gender: 'male' | 'female'): Promise<any>;
  getProfile(id: string): Promise<FullProfile>;
  getMyProfile(): Promise<FullProfile>;
  uploadPhoto(file: File): Promise<{ url: string }>;
  deletePhoto(photoUrl: string): Promise<void>;
  
  // Discovery feed
  getFeedProfiles(limit?: number, cursor?: string): Promise<PaginatedResponse<ProfileSummary>>;
  swipeProfile(request: SwipeRequest): Promise<SwipeResponse>;
  
  // Matches
  getMatch(matchId: string): Promise<Match>;
  unmatch(matchId: string): Promise<void>;
  starMatch(matchId: string, starred: boolean): Promise<void>;
  
  // Messages
  getMessages(matchId: string, limit?: number, cursor?: string): Promise<PaginatedResponse<Message>>;
  sendMessage(matchId: string, text: string): Promise<Message>;
  markMessagesRead(matchId: string, messageIds: string[]): Promise<void>;
  
  // Messaging 2.0 - новые методы
  sendMessageV2(matchId: number, data: MessageCreate): Promise<Message>;
  editMessage(messageId: number, content: string): Promise<Message>;
  deleteMessage(messageId: number): Promise<{ success: boolean; message: string }>;
  reportMessage(messageId: number, reason: string): Promise<{ success: boolean; message: string; report_id: number }>;
  
  // User actions
  reportUser(userId: string, reason: string): Promise<void>;
  blockUser(userId: string): Promise<void>;
  getBlockedUsers(): Promise<any>;
  unblockUser(userId: string): Promise<void>;
  
  // Premium features
  rewind(): Promise<ProfileSummary | null>;
  boost(): Promise<{ expiresAt: string }>;
  
  // Settings
  updatePreferences(preferences: any): Promise<void>;
  deleteAccount(): Promise<void>;

  // IT Verification
  getVerificationStatus(): Promise<VerificationStatusResponse>;
  startVerificationTest(data: { specialization_id: number; declared_level: string }): Promise<StartTestResponse>;
  getTestData(testId: number): Promise<TestDataResponse>;
  submitTestAnswers(data: SubmitAnswersRequest): Promise<SubmitAnswersResponse>;
  getTestResult(testId: number): Promise<TestResultResponse>;
  waitForTestQuestions(testId: number, onProgress?: (attempt: number) => void, maxAttempts?: number, intervalMs?: number): Promise<TestDataResponse>;
  waitForTestResult(testId: number, onProgress?: (attempt: number) => void, maxAttempts?: number, intervalMs?: number): Promise<TestResultResponse>;

  // Discovery & Swipes
  getDiscoveryCards(limit?: number): Promise<CardResponse[]>;
  swipeUser(data: SwipeCreateRequest): Promise<SwipeResponseData>;
  rewindSwipe(): Promise<{ success: boolean; message: string }>;
  getLikes(): Promise<LikesResponse>;

  // Matches
  getMatches(): Promise<MatchResponse[]>;
  unmatchUser(matchId: number): Promise<{ success: boolean; message: string }>;

  // Messages
  getMatchMessages(matchId: number, limit?: number, offset?: number): Promise<MessageResponse[]>;
  sendMatchMessage(matchId: number, content: string): Promise<MessageResponse>;
  markMessageAsRead(messageId: number): Promise<{ success: boolean }>;

  // Notifications
  getNotificationCounters(): Promise<NotificationCounters>;

  // Subscriptions
  getSubscriptionStatus(): Promise<SubscriptionStatusResponse>;

  // My Likes (outgoing likes)
  getMyLikes(): Promise<MyLikesResponse>;
  removeLike(data: RemoveLikeRequest): Promise<RemoveLikeResponse>;

  // Filters
  getBasicFilters(): Promise<BasicFiltersResponse>;
  updateBasicFilters(filters: Partial<BasicFilters>): Promise<void>;
  resetBasicFilters(): Promise<void>;

  // Dictionaries
  getLifestyleTags(): Promise<any[]>;
  getMeetingGoals(): Promise<any[]>;

  // Browse API (alternative feed)
  browseProfiles(limit?: number, lastId?: number): Promise<import('@/types').BrowseCardResponse[]>;
  updateSwipe(targetUserId: number, action: 'like' | 'pass' | 'superlike', hide?: number): Promise<import('@/types').SwipeResponseData>;
  deleteLike(targetUserId: number): Promise<{ success: boolean; message: string }>;
  
  // Upload photo only (for chat messages)
  uploadPhotoOnly(file: File): Promise<{ url: string }>;
}

/**
 * Base API client with common functionality
 */
export abstract class BaseApiClient implements ITinderApiClient {
  protected baseUrl: string;
  protected token?: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  setToken(token: string): void {
    this.token = token;
  }

  protected getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    return headers;
  }

  protected async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Handle 401 Unauthorized - token expired or invalid
        if (response.status === 401) {
          console.warn('🔓 401 Unauthorized - token expired or invalid');
          
          // Create error with status code for proper handling upstream
          const error = await response.json().catch(() => ({ 
            message: 'Unauthorized' 
          }));
          const err = new Error(error.message || 'HTTP 401 Unauthorized');
          (err as any).status = 401;
          throw err;
        }
        
        // Handle 403 Forbidden - redirect to splash/login
        if (response.status === 403) {
          console.warn('🚫 403 Forbidden - clearing all data and redirecting');
          
          // Clear all local storage
          localStorage.clear();
          sessionStorage.clear();
          this.token = undefined;
          
          // Clear IndexedDB (used by zustand persist)
          if (window.indexedDB) {
            try {
              const databases = await window.indexedDB.databases();
              databases.forEach(db => {
                if (db.name) window.indexedDB.deleteDatabase(db.name);
              });
            } catch (err) {
              console.warn('Failed to clear IndexedDB:', err);
            }
          }
          
          // Dynamically import store to avoid circular dependency
          setTimeout(async () => {
            try {
              const { useAppStore } = await import('@/store');
              const { reset } = useAppStore.getState();
              reset();
              
              // Force reload to clear everything
              window.location.reload();
            } catch (err) {
              console.error('Failed to handle 403 redirect:', err);
              window.location.reload();
            }
          }, 100);
          
          throw new Error('Session expired. Please login again.');
        }
        
        const error = await response.json().catch(() => ({ 
          message: 'An error occurred' 
        }));
        const err = new Error(error.message || `HTTP ${response.status}`);
        (err as any).status = response.status;
        throw err;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return response.text() as any;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  protected async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  protected async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  protected async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  protected async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Abstract methods to be implemented by concrete classes
  abstract bootstrap(initData?: string): Promise<BootstrapResponse>;
  abstract createProfile(data: Partial<OnboardingData>): Promise<FullProfile>;
  abstract updateProfile(data: Partial<FullProfile>): Promise<FullProfile>;
  abstract updateGender(gender: 'male' | 'female'): Promise<any>;
  abstract getProfile(id: string): Promise<FullProfile>;
  abstract getMyProfile(): Promise<FullProfile>;
  abstract uploadPhoto(file: File): Promise<{ url: string }>;
  abstract deletePhoto(photoUrl: string): Promise<void>;
  abstract getFeedProfiles(limit?: number, cursor?: string): Promise<PaginatedResponse<ProfileSummary>>;
  abstract swipeProfile(request: SwipeRequest): Promise<SwipeResponse>;
  abstract getMatch(matchId: string): Promise<Match>;
  abstract unmatch(matchId: string): Promise<void>;
  abstract starMatch(matchId: string, starred: boolean): Promise<void>;
  abstract getMessages(matchId: string, limit?: number, cursor?: string): Promise<PaginatedResponse<Message>>;
  abstract sendMessage(matchId: string, text: string): Promise<Message>;
  abstract markMessagesRead(matchId: string, messageIds: string[]): Promise<void>;
  abstract sendMessageV2(matchId: number, data: MessageCreate): Promise<Message>;
  abstract editMessage(messageId: number, content: string): Promise<Message>;
  abstract deleteMessage(messageId: number): Promise<{ success: boolean; message: string }>;
  abstract reportMessage(messageId: number, reason: string): Promise<{ success: boolean; message: string; report_id: number }>;
  abstract reportUser(userId: string, reason: string): Promise<void>;
  abstract blockUser(userId: string): Promise<void>;
  abstract getBlockedUsers(): Promise<any>;
  abstract unblockUser(userId: string): Promise<void>;
  abstract rewind(): Promise<ProfileSummary | null>;
  abstract boost(): Promise<{ expiresAt: string }>;
  abstract updatePreferences(preferences: any): Promise<void>;
  abstract deleteAccount(): Promise<void>;
  abstract getVerificationStatus(): Promise<VerificationStatusResponse>;
  abstract startVerificationTest(data: { specialization_id: number; declared_level: string }): Promise<StartTestResponse>;
  abstract getTestData(testId: number): Promise<TestDataResponse>;
  abstract submitTestAnswers(data: SubmitAnswersRequest): Promise<SubmitAnswersResponse>;
  abstract getTestResult(testId: number): Promise<TestResultResponse>;
  abstract waitForTestQuestions(testId: number, onProgress?: (attempt: number) => void, maxAttempts?: number, intervalMs?: number): Promise<TestDataResponse>;
  abstract waitForTestResult(testId: number, onProgress?: (attempt: number) => void, maxAttempts?: number, intervalMs?: number): Promise<TestResultResponse>;
  abstract getDiscoveryCards(limit?: number): Promise<CardResponse[]>;
  abstract swipeUser(data: SwipeCreateRequest): Promise<SwipeResponseData>;
  abstract rewindSwipe(): Promise<{ success: boolean; message: string }>;
  abstract getLikes(): Promise<LikesResponse>;
  abstract getMatches(): Promise<MatchResponse[]>;
  abstract unmatchUser(matchId: number): Promise<{ success: boolean; message: string }>;
  abstract getMatchMessages(matchId: number, limit?: number, offset?: number): Promise<MessageResponse[]>;
  abstract sendMatchMessage(matchId: number, content: string): Promise<MessageResponse>;
  abstract markMessageAsRead(messageId: number): Promise<{ success: boolean }>;
  abstract getNotificationCounters(): Promise<NotificationCounters>;
  abstract getSubscriptionStatus(): Promise<SubscriptionStatusResponse>;
  abstract getMyLikes(): Promise<MyLikesResponse>;
  abstract removeLike(data: RemoveLikeRequest): Promise<RemoveLikeResponse>;
  abstract getBasicFilters(): Promise<BasicFiltersResponse>;
  abstract updateBasicFilters(filters: Partial<BasicFilters>): Promise<void>;
  abstract resetBasicFilters(): Promise<void>;
  abstract getLifestyleTags(): Promise<any[]>;
  abstract getMeetingGoals(): Promise<any[]>;
  abstract browseProfiles(limit?: number, lastId?: number): Promise<import('@/types').BrowseCardResponse[]>;
  abstract updateSwipe(targetUserId: number, action: 'like' | 'pass' | 'superlike', hide?: number): Promise<import('@/types').SwipeResponseData>;
  abstract deleteLike(targetUserId: number): Promise<{ success: boolean; message: string }>;
  abstract uploadPhotoOnly(file: File): Promise<{ url: string }>;
}
